'use strict';

var KayleeBase = require('./KayleeBase');
var KayleeCalls = require('./KayleeCalls');
var KayleeEquipmentTypes = require('./KayleeEquipmentTypes');
var log = require('../log/Log').channel('KayleeAnalytics');
var moment = require('moment');

var KayleeAnalytics = {
    getAnalyticsData: getAnalyticsData,
};

var cache = {};
var cachePromises = {};
var authHandlerRegistered = false;
var uid = null;

// cache = {
//     <nodeId.collection>: <response rows>,
//     ...
// }

function addToCache(key, rows) {
    if (!authHandlerRegistered) {
        authHandlerRegistered = true;
        KayleeBase.onAuthStateChanged(function (user) {
            if (!user || (uid !== null && uid !== user.uid)) {
                log.debug('clearing analytics cache');
                cache = {};
            }

            uid = user ? user.uid : null;
        });
    }

    cache[key] = rows;
}

function assembleData(rows, bins, indices, collection, count, groupInfo) {
    // Assume rows, indices, and bins are in ascending order by time.
    var j = 0; // index into indices
    var g;
    var obj = {};
    var values = {};

    groupInfo.names.forEach(function (group) {
        obj[group] = [];
        values[group] = 0;
    });

    obj.timestamps = [];

    var row = rows[indices[j]];

    for (var b = 1; b < bins.length && j < indices.length; b++) {
        while (row.time < bins[b]) {
            var value;

            if (collection === 'roomOfflineEvents' && count === 'new') {
                value = row.onlineState === 'offline' ? 0 : 1;
            } else if (count === 'duration') {
                value = row.duration;
            } else if (count === 'total') {
                value = row.count;
            } else {
                value = 1;
            }

            if (groupInfo.column === null) {
                values.all += value;
            } else {
                if (groupInfo.hasOwnProperty('translate')) {
                    values[groupInfo.translate(row[groupInfo.column])] += value;
                } else {
                    values[row[groupInfo.column]] += value;
                }
            }

            j++;
            if (j >= indices.length) break;
            row = rows[indices[j]];
        }

        obj.timestamps.push(bins[b - 1] / 1000);

        for (g = 0; g < groupInfo.names.length; g++) {
            obj[groupInfo.names[g]].push(values[groupInfo.names[g]]);

            if (count !== 'total') {
                values[groupInfo.names[g]] = 0;
            }
        }
    }

    return obj;
}

// bin = 'minute', 'hour', 'day', 'week', 'month', 'quarter', 'year'
// return value is an array of binned timestamps starting with the first bin
// before the earliest date and ending with the last bin after the latest date

function computeBins(rows, bin, indices) {
    if (indices.length <= 0) return [];
    // Assume rows and indices are in ascending order by time.
    var momentMin = moment(rows[indices[0]].time).startOf(bin + 's');
    var momentMax = moment(rows[indices[indices.length - 1]].time)
        .add(1, bin)
        .startOf(bin + 's');
    var bins = [];
    var m = momentMin.clone();

    while (m.isBefore(momentMax)) {
        bins.push(m.valueOf());
        m.add(1, bin);
    }

    bins.push(momentMax.valueOf());
    return bins;
}

function computeRoomOfflineDurations(rows, bin, bins, indices) {
    var b = 0;
    var info = {};
    var newRows = [];

    for (var i = 0; i <= indices.length; i++) {
        var row = i === indices.length ? null : rows[indices[i]];
        var nextBin = moment(bins[b]).add(1, bin).valueOf();

        while (row === null || row.time >= nextBin) {
            for (var roomId in info) {
                if (!info.hasOwnProperty(roomId)) continue;

                var inf = info[roomId];
                var newRow = {
                    time: bins[b],
                    companyId: info[roomId].companyId,
                    siteId: info[roomId].siteId,
                    roomId: info[roomId].roomId,
                };

                if (inf.hasOwnProperty('time')) {
                    newRow.duration = inf.duration + nextBin - info[roomId].time;
                    info[roomId].time = nextBin;
                    info[roomId].duration = 0;
                } else {
                    newRow.duration = inf.duration;
                    delete info[roomId];
                }

                newRows.push(newRow);
            }

            b++;
            nextBin = moment(bins[b]).add(1, bin).valueOf();

            if (row === null || b >= bins.length) break;
        }

        if (row === null) continue;

        if (row.onlineState === 'online') {
            if (info.hasOwnProperty(row.roomId) && info[row.roomId].hasOwnProperty('time')) {
                info[row.roomId].duration += row.time - info[row.roomId].time;
                delete info[row.roomId].time;
            }
        } else {
            // offline or limited
            if (info.hasOwnProperty(row.roomId)) {
                if (!info[row.roomId].hasOwnProperty('time')) {
                    info[row.roomId].time = row.time;
                }
            } else {
                info[row.roomId] = {
                    companyId: row.companyId,
                    siteId: row.siteId,
                    roomId: row.roomId,
                    time: row.time,
                    duration: 0,
                };
            }
        }
    }

    return newRows;
}

function filterRows(rows, filters, indices) {
    if (filters.length <= 0) return indices;

    var newIndices = [];

    var resultFilters = [];
    var dialMethodFilters = [];
    var directionFilters = [];
    var callTypeFilters = [];
    var equipmentTypeFilters = [];

    filters.forEach(function (filter) {
        if (filter === 'missed' || filter === 'answered') {
            resultFilters.push(filter);
        } else if (filter === 'calendar' || filter === 'manual') {
            dialMethodFilters.push(filter);
        } else if (filter === 'incoming' || filter === 'outgoing') {
            directionFilters.push(filter);
        } else if (KayleeCalls.calltypes.includes(filter)) {
            callTypeFilters = [];
        } else if (KayleeEquipmentTypes.equipmentTypeTypes.includes(filter)) {
            equipmentTypeFilters.push(filter);
        }
    });

    indices.forEach(function (i) {
        var keep = true;

        if (resultFilters.length > 0 && !resultFilters.includes(rows[i].result)) {
            keep = false;
        }

        if (dialMethodFilters.length > 0 && !dialMethodFilters.includes(rows[i].dialMethod)) {
            keep = false;
        }

        if (directionFilters.length > 0 && !directionFilters.includes(rows[i].direction)) {
            keep = false;
        }

        if (callTypeFilters.length > 0 && !callTypeFilters.includes(rows[i].callType)) {
            keep = false;
        }

        if (equipmentTypeFilters.length > 0 && !equipmentTypeFilters.includes(rows[i].type)) {
            keep = false;
        }

        if (keep) newIndices.push(i);
    });

    return newIndices;
}

function formatData(obj, format) {
    if (format === 'rows') return obj;

    var newObj = {};

    Object.keys(obj).forEach(function (group) {
        if (group === 'timestamps') return;
        newObj[group] = [];

        for (var i = 0; i < obj.timestamps.length; i++) {
            if (format === 'tyobject') {
                newObj[group].push({ t: obj.timestamps[i], y: obj[group][i] });
            } else if (format === 'xyarray') {
                newObj[group].push([obj.timestamps[i], obj[group][i]]);
            } else if (format === 'xyobject') {
                newObj[group].push({ x: obj.timestamps[i], y: obj[group][i] });
            }
        }
    });

    return newObj;
}

// getAnalyticsData(node, collection, bin, count, group, filters, format, maxGroups)
//
// node: null, undefined or object with $id and $nodeType = 'company', 'site',
//     or 'room'
// collection: 'calls', 'equipment', 'companies', 'sites', 'rooms', or
//     'roomOfflineEvents'
// bin: 'minute', 'day', 'week', 'month', 'quarter', 'year'
// count:
//     for calls: 'duration', 'new'
//     for equipment, companies, sites, rooms: 'total'
//     for roomOfflineEvents: 'duration', 'new'
// group:
//     for calls:
//         null (all calls)
//         type (pstn, video, voip, zoom)
//         result (missed, answered)
//         dialMethod (calendar, manual)
//         direction (incoming, outgoing)
//         company (company ids...)
//         site (site ids...)
//         room (room ids...)
//     for equipment:
//         null (all equipment)
//         manufacturer (Polycom, NEC, etc.)
//         model (Polycom Group Series, NEC Multisync, etc.)
//         type (videoCodec, display, etc.)
//         company (company ids...)
//         site (site ids...)
//         room (room ids...)
//     for companies:
//         null (all companies)
//     for sites:
//         null (all sites)
//         company (company ids...)
//     for rooms:
//         null (all rooms)
//         company (company ids...)
//         site (site ids...)
//     for roomOfflineEvents:
//         null (all rooms)
//         company (company ids...)
//         site (site ids...)
//         room (room ids...)
// filters: null, undefined or array of:
//     for calls: 'missed', 'answered', 'calendar', 'manual', 'incoming',
//         'outgoing', 'pstn', 'video', 'voip', 'zoom'
//     for equipment: 'audioDsp', 'camera', 'display', 'videoCodec',
//         'videoSwitcher'
//     for companies, sites, rooms, roomOfflineEvents: no currently suppported
//         filters
// format:
//     'rows' or null: {
//         timestamps: [ t0, t1, t2, ... ]
//         <name 1>: [ y0, y1, y2, ... ]
//         <name 2>: [ y0, y1, y2, ... ]
//         ...
//     }
//      'xyobject': {
//          <name 1>: [ { x: t0, y: y0 }, { x: t1, y: y1 }, { x: t2, y: y2 }, ... ]
//          <name 2>: [ { x: t0, y: y0 }, { x: t1, y: y1 }, { x: t2, y: y2 }, ... ]
//          ...
//      }
//      'tyobject': {
//          <name 1>: [ { t: t0, y: y0 }, { t: t1, y: y1 }, { t: t2, y: y2 }, ... ]
//          <name 2>: [ { t: t0, y: y0 }, { t: t1, y: y1 }, { t: t2, y: y2 }, ... ]
//          ...
//      }
//     'xyarray': {
//         <name 1>: [ [ t0, y0 ], [ t1, y1 ], [ t2, y2 ], ... ]
//         <name 2>: [ [ t0, y0 ], [ t1, y1 ], [ t2, y2 ], ... ]
//         ...
//      }
// maxGroups:
//     Limit the maximum number of groups to this value. The groups with the
//     highest maximum values will be kept in the output; all other groups will
//     be combined into an "other" group.
//
// Returns a promise with a value in the specified format:
//
// Example 1: Get missed calls binned by month and grouped by call type.
//
//     getAnalyticsData(null, 'calls', 'month', 'new', 'type', [ 'missed' ]);
//
//     return value = {
//         timestamps: array of timestamps for the beginning of each month
//         pstn: array of values indicating the number of missed pstn calls for
//             that month
//         video: array of values indicating the number of missed video calls
//             for that month
//         voip: array of values indicating the number of missed voip calls
//             for that month
//         zoom: array of values indicating the number of missed zoom calls
//             for that month
//     }
//
// Example 2: Get number of calls dialed manually and via calendar event each month.
//
//     getAnalyticsData(null, 'calls', 'month', 'new', 'dialMethod');
//
//     return value = {
//         timestamps: array of timestamps for the beginning of each month
//         calendar: array of values indicaating the number of calls dialed
//             via calendar event for that month
//         manual: array of values indicating the number of manually dialed
//             calls for that month
//     }
//
// Example 3: Get total time spent in each type of call per month
//
//     getAnalyticsData(null, 'calls', 'month', 'duration', 'type');
//
//     return value = {
//         timestamps: array of timestamps for the beginning of each month
//         pstn: array of values indicating the number of milliseconds spent
//             in pstn calls for that month
//         video: array of values indicating the number of milliseconds
//             spent in video calls for that month
//         voip: array of values indicating the number of milliseconds spent
//             in voip calls for that month
//         zoom: array of values indicating the number of milliseconds spent
//             in zoom calls for that month
//     }
//
// Example 4: Get number of rooms per month.
//
//     getAnalyticsData(null, 'rooms', 'month', 'total');
//
//     return value = {
//         timestamps: array of timestamps for the beginning of each month
//         total: array of values indicating the number of rooms for that month
//     }

function getAnalyticsData(node, collection, bin, count, group, filters, format, maxGroups) {
    var nodeInfo;

    if (typeof format === 'undefined' || format === null) format = 'rows';

    if (typeof node !== 'undefined' && node !== null) {
        nodeInfo = { $nodeType: node.$nodeType, $id: node.$id };
    }

    if (typeof filters === 'undefined' || filters === null) filters = [];

    if (typeof maxGroups === 'undefined' || maxGroups === null) maxGroups = 5;

    return Promise.resolve()
        .then(KayleeEquipmentTypes.getEquipmentTypes().$loaded)
        .then(function () {
            return getFromCache(null, collection);
        })
        .then(function (rows) {
            var indices = getIndicesForNode(rows, nodeInfo);
            indices = filterRows(rows, filters, indices);

            var bins = computeBins(rows, bin, indices);

            if (collection === 'roomOfflineEvents' && count === 'duration') {
                rows = computeRoomOfflineDurations(rows, bin, bins, indices);
                indices = [];
                rows.forEach(function (row, index) {
                    indices.push(index);
                });
            }

            var groupInfo = getGroupInfo(rows, indices, collection, group);
            var obj = assembleData(rows, bins, indices, collection, count, groupInfo);
            obj = limitGroups(obj, maxGroups);
            obj = formatData(obj, format);
            return obj;
        });
}

function getFromCache(nodeInfo, collection) {
    var nodeId = typeof nodeInfo === 'undefined' || nodeInfo === null ? null : nodeInfo.$id;
    var key = makeKey(nodeId, collection);

    if (cache.hasOwnProperty(key)) {
        return Promise.resolve(cache[key]);
    }

    var deferredPromise = {};

    deferredPromise.promise = new Promise(function (resolve, reject) {
        deferredPromise.resolve = resolve;
        deferredPromise.reject = reject;
    });

    if (!cachePromises.hasOwnProperty(key)) cachePromises[key] = [];
    cachePromises[key].push(deferredPromise);

    if (cachePromises[key].length === 1) {
        Promise.resolve()
            .then(function () {
                if (collection === 'roomOfflineEvents') {
                    return getRoomOfflineEvents(nodeInfo);
                }

                return KayleeBase.request('analyticsRequest', {
                    node: nodeInfo,
                    collection: {
                        calls: 'calls',
                        equipment: 'equipmentCreatedEvents',
                        companies: 'companyCreatedEvents',
                        sites: 'siteCreatedEvents',
                        rooms: 'roomCreatedEvents',
                        controllerPairingEvents: 'controllerPairingEvents',
                    }[collection],
                }).then(function (response) {
                    return response.rows[0];
                });
            })
            .then(function (rows) {
                addToCache(key, rows);
                cachePromises[key].forEach(function (deferredPromise) {
                    deferredPromise.resolve(rows);
                });
                delete cachePromises[key];
            })
            .catch(function (err) {
                log.error('getFromCache: ' + err);
                cachePromises[key].forEach(function (deferredPromise) {
                    deferredPromise.reject(err);
                });
                delete cachePromises[key];
            });
    }

    return deferredPromise.promise;
}

function getGroupInfo(rows, indices, collection, group) {
    if (collection === 'calls' && group === 'type') {
        return {
            names: KayleeCalls.callTypes,
            column: 'callType',
        };
    } else if (group === 'result') {
        return {
            names: ['answered', 'missed'],
            column: 'result',
        };
    } else if (group === 'dialMethod') {
        return {
            names: ['calendar', 'manual'],
            column: 'dialMethod',
        };
    } else if (group === 'direction') {
        return {
            names: ['incoming', 'outgoing'],
            column: 'direction',
        };
    } else if (group === 'company') {
        return {
            names: getGroupsFromColumn(rows, indices, 'companyId'),
            column: 'companyId',
        };
    } else if (group === 'site') {
        return {
            names: getGroupsFromColumn(rows, indices, 'siteId'),
            column: 'siteId',
        };
    } else if (group === 'room') {
        return {
            names: getGroupsFromColumn(rows, indices, 'roomId'),
            column: 'roomId',
        };
    } else if (group === 'manufacturer') {
        return {
            names: getGroupsFromColumn(rows, indices, 'type', function (eqTypeId) {
                return KayleeEquipmentTypes.getEquipmentTypes().$recordAt(eqTypeId).manufacturer;
            }),
            column: 'type',
            translate: function (eqTypeId) {
                return KayleeEquipmentTypes.getEquipmentTypes().$recordAt(eqTypeId).manufacturer;
            },
        };
    } else if (group === 'model') {
        return {
            names: getGroupsFromColumn(rows, indices, 'type', function (eqTypeId) {
                var eqType = KayleeEquipmentTypes.getEquipmentTypes().$recordAt(eqTypeId);
                if (eqType === null) log.error('invalid equipment type = ' + eqTypeId);
                return eqType.manufacturer + ' ' + eqType.model;
            }),
            column: 'type',
            translate: function (eqTypeId) {
                var eqType = KayleeEquipmentTypes.getEquipmentTypes().$recordAt(eqTypeId);
                return eqType.manufacturer + ' ' + eqType.model;
            },
        };
    } else if (collection === 'equipment' && group === 'type') {
        return {
            names: getGroupsFromColumn(rows, indices, 'type', function (eqTypeId) {
                return KayleeEquipmentTypes.getEquipmentTypes().$recordAt(eqTypeId).type;
            }),
            column: 'type',
            translate: function (eqTypeId) {
                return KayleeEquipmentTypes.getEquipmentTypes().$recordAt(eqTypeId).type;
            },
        };
    } else if (group === null) {
        return {
            names: ['all'],
            column: null,
        };
    }
}

function getGroupsFromColumn(rows, indices, name, translate) {
    var groups = {};
    var j;

    if (typeof translate === 'function') {
        for (j = 0; j < indices.length; j++) {
            groups[translate(rows[indices[j]][name])] = true;
        }
    } else {
        for (j = 0; j < indices.length; j++) {
            groups[rows[indices[j]][name]] = true;
        }
    }

    return Object.keys(groups);
}

function getIndicesForNode(rows, node) {
    var indices = [];

    for (var i = 0; i < rows.length; i++) {
        if (
            typeof node === 'undefined' ||
            node === null ||
            (node.$nodeType === 'company' && rows[i].companyId === node.$id) ||
            (node.$nodeType === 'site' && rows[i].siteId === node.$id) ||
            (node.$nodeType === 'room' && rows[i].roomId === node.$id)
        ) {
            indices.push(i);
        }
    }

    return indices;
}

function getRoomOfflineEvents(nodeInfo) {
    // For room offline events, we also merge in room deleted events and treat
    // them as room online events. This prevents a room from appearing offline
    // forever if it gets deleted while it is offline. We also merge in
    // controller unpaired events and treat them as room offline events for the
    // same reason.

    var promises = [
        KayleeBase.request('analyticsRequest', {
            node: nodeInfo,
            collection: 'roomOnlineStateEvents',
        }).then(function (response) {
            return response.rows[0];
        }),
        getFromCache(nodeInfo, 'rooms'),
        getFromCache(nodeInfo, 'controllerPairingEvents'),
    ];

    return Promise.all(promises).then(function (results) {
        var roomOnlineStateEvents = results[0];
        var roomCreatedEvents = results[1];
        var controllerPairingEvents = results[2];

        var rows = roomOnlineStateEvents;
        var newRows = [];
        var i,
            j = 0;

        for (i = 0; i < rows.length; i++) {
            while (j < roomCreatedEvents.length && roomCreatedEvents[j].time < rows[i].time) {
                if (roomCreatedEvents[j].count < 0) {
                    newRows.push({
                        time: roomCreatedEvents[j].time,
                        companyId: roomCreatedEvents[j].companyId,
                        siteId: roomCreatedEvents[j].siteId,
                        roomId: roomCreatedEvents[j].roomId,
                        onlineState: 'online',
                    });
                }

                j++;
            }

            newRows.push(rows[i]);
        }

        rows = newRows;
        newRows = [];
        j = 0;

        for (i = 0; i < rows.length; i++) {
            while (
                j < controllerPairingEvents.length &&
                controllerPairingEvents[j].time < rows[i].time
            ) {
                if (controllerPairingEvents[j].count < 0) {
                    newRows.push({
                        time: controllerPairingEvents[j].time,
                        companyId: controllerPairingEvents[j].companyId,
                        siteId: controllerPairingEvents[j].siteId,
                        roomId: controllerPairingEvents[j].roomId,
                        onlineState: 'online',
                    });
                }

                j++;
            }

            newRows.push(rows[i]);
        }

        return newRows;
    });
}

function makeKey(nodeId, collection) {
    var key = '';

    key += typeof nodeId === 'undefined' || nodeId === null ? 'null' : nodeId;
    key += '.' + collection;

    return key;
}

function limitGroups(obj, maxGroups) {
    var groups = Object.keys(obj);
    var i = groups.indexOf('timestamps');
    groups.splice(i, 1);

    if (groups.length < maxGroups) return obj;

    var maximums = {};

    groups.forEach(function (group) {
        maximums[group] = obj[group][0];

        for (i = 1; i < obj[group].length; i++) {
            if (obj[group][i] > maximums[group]) maximums[group] = obj[group][i];
        }
    });

    groups.sort(function (a, b) {
        // sort in descending order
        return maximums[a] - maximums[b];
    });

    var other = [];

    for (i = 0; i < groups.length - maxGroups + 1; i++) {
        for (var j = 0; j < obj.timestamps.length; j++) {
            if (i === 0) other[j] = 0;
            other[j] += obj[groups[i]][j];
        }

        delete obj[groups[i]];
    }

    obj.other = other;

    return obj;
}

module.exports = KayleeAnalytics;
